<template>
  <div v-if="pending" class="el-button is-disabled mb-2 me-2" :class="[buttonClass, className]">
    <i class="loader material-icons md-18">sync</i>
  </div>
  <div v-else-if="writeOrRead" :class="[buttonContainerClass]" :key="`${url}${componentKey}`">
    <a
      class="el-button btn text-truncate d-flex flex-direction-row gap-2 justify-content-center"
      @click.prevent="onClick"
      :style="buttonStyle"
      :class="[anchorClass, className]"
      :data-id="buttonId"
    >
      <slot>
        <i :class="icon" class="pr5" v-if="icon"></i>
        {{ buttonName }}
      </slot>
    </a>
  </div>
</template>

<script>
/**
 * @param CommonAnchorButton component
 *
 * @description A listák gombjainak dinamikus megjelenítésére szolgáló component
 */
import { mapActions } from 'vuex'
import { get, toArray, isUndefined, isString } from 'lodash'
import EventBus from '../../../utils/event-bus'
import ExpressionResolver from '../../../store/modules/form/helpers/expression-resolver-store'
import Loader from '../../Loader'
import confirmation from '../../../utils/mixin-confirmation'

export default {
  name: 'CommonAnchorButton',
  props: [
    'className',
    'size',
    'buttonStyle',
    'layoutDirection',
    'buttonListType',
    'isInExpanded',
    'currentViewName',
    'isDetailViewVisible',
    'icon',
    'name',
    'url',
    'confirm',
    'listCollapsedButton',
    'popup',
    'formType',
    'rowOptions',
    'target',
    'createNewElement',
    'permission',
    'type',
    'groupReadOnly',
    'readOnly',
    'emitEvent',
    'remoteFieldPath',
    'containerClassName',
  ],
  confirmation,
  components: {
    Loader,
  },
  async created() {
    if (!this.permission) {
      const response = await this.getPermissions({ url: this.url })
      this.perm = get(response, 'body')
    } else {
      this.perm = this.permission
    }
    this.pending = false
    this.confirmMessage = await this.getConfirmMessage()
  },
  mounted() {
    EventBus.$on('refresh-panel', async () => {
      const response = await this.getPermissions({ url: this.url })
      this.perm = get(response, 'body')
      this.componentKey++
    })
  },
  beforeDestroy() {
    EventBus.$off('refresh-panel')
  },
  methods: {
    ...mapActions('popup', ['setOpened', 'setUrl', 'setType', 'setCreateNewElement', 'isOpened']),
    async onClick() {
      if (this.isPopup) {
        this.setUrl({
          url: this.url,
          event: isString(this.emitEvent) ? this.emitEvent : Object.assign({}, this.emitEvent, this.remoteFieldPath),
        })
        this.setType(this.formType)
        this.setCreateNewElement(this.createNewElement)
        if (this.createNewElement) {
          this.$emit('click-create-new-element')
        }
        this.setOpened()
        EventBus.$emit('refresh-form-expression', {})
        return false
      } else {
        await this.handleConfirmation()
      }
    },
    async handleConfirmation() {
      let confirmed = true
      if (this.confirmMessage) {
        confirmed = await this._confirm(this.confirmMessage)
      }
      if (confirmed) window.open(this.url, this.target)
    },
    async getPermissions(parameters) {
      this.pending = true
      return this.$client.execute({
        operationId: 'Authorities_getPermissionByUrl',
        parameters,
      })
    },
    param(param) {
      const parameters = {}
      const hiddenSearchData = get(param, 'view.properties.hiddenSearchData')

      if (hiddenSearchData) {
        hiddenSearchData.where.forEach((e) => {
          parameters['pk'] = e
        })
      }

      const mongoId = get(param, 'view.properties.mongoId')
      if (mongoId) {
        parameters['mongoId'] = mongoId
      }

      return parameters
    },
    async getConfirmMessage() {
      if (this.type === 'nodeProcess' && isUndefined(this.confirm)) {
        const id = this.url.match(/id=(.*)&modelID=/)[1] // @regex process url-ekből a processz mongo-s id-jára match-el

        const response = await this.$client.execute({
          operationId: 'ProcessBuilder_findOne',
          parameters: {
            filter: JSON.stringify({
              where: { id },
            }),
          },
        })

        const process = response.body

        if (process && process['3--confirm']) {
          const active = await ExpressionResolver.resolve(process['3--confirm'])
          if (active) {
            return process['3--confirmMessage']
          }
        }
      }

      return this.confirm
    },
    translateParams(params) {
      if (!params) return
      const translateParams = {}
      Object.keys(params).forEach((key) => {
        translateParams[key] = this.$i18n.t(params[key])
      })
      return translateParams
    },
  },
  data() {
    return {
      pending: false,
      perm: null,
      componentKey: 0,
      parameters: {},
      confirmMessage: '',
    }
  },
  computed: {
    buttonContainerClass() {
      if (this.layoutDirection === 'horizontal-button-layout') return 'horizontal-button-margin'
      if ((this.isInExpanded && !this.isDetailViewVisible) || this.currentViewName === 'detailView')
        return 'col-md-6 split'
      if (this.containerClassName) return this.containerClassName
    },
    anchorClass() {
      let classString = ''
      if (!this.className) classString += this.name === 'Delete' ? ' el-button--danger' : ' el-button--default is-plain'
      if (this.isInExpanded) {
        classString += ' el-button--small'
      } else if (this.size) {
        classString += ` el-button--${this.size}`
      }
      return classString
    },
    buttonClass() {
      let classString = ''
      if (this.layoutDirection === 'vertical-button-layout') classString += ' vertical-button-padding'
      if (this.isInExpanded) {
        classString += ' el-button--small p-1'
      } else {
        classString += ' el-button--small'
      }
      return classString
    },
    buttonName() {
      return typeof this.name === 'string'
        ? this.$i18n.t(this.name)
        : this.$i18n.interpolate(
            this.$i18n.t(this.name.text, { category: 'CommonAnchorButton' }),
            this.translateParams(this.name.params)
          )
    },
    buttonId() {
      return typeof this.name === 'string' ? this.name : get(this.name, 'params.element', '')
    },
    isPopup() {
      return this.$attrs.popup || this.popup
    },
    writeOrRead() {
      if (!this.groupReadOnly && !this.readOnly && this.perm) {
        const permissionValues = Object.values(this.perm)
        const permissions = get(permissionValues, '0')
        return toArray(permissions).every((el) => el == 1)
      } else {
        return false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.horizontal-button-margin {
  margin: 2px;
}
.vertical-button-padding {
  padding: 3px;
}
.split {
  padding: 0 1px;
}
.el-button {
  width: 100%;
  &.is-disabled {
    border-style: dashed;
  }
}
@keyframes rotating {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.loader {
  transform: rotate(0);
  animation-name: rotating;
  animation-duration: 0.75s;
  animation-iteration-count: infinite;
}
</style>
